import styled, { css } from 'styled-components';

export const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 20px;
  height: calc(100vh - 160px);
  min-height: 560px;
  font-family: 'Plus Jakarta Sans', sans-serif;

  @media (max-width: 1200px) {
    grid-template-columns: minmax(0, 1fr) 300px;
  }

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    height: auto;
    min-height: 0;
  }
`;

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
`;

export const ChatHeader = styled.header`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #eef2f7;
  background: #ffffff;
`;

export const ProtocolBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;

  .label {
    font-size: 11px;
    font-weight: 600;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .value {
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

type StatusTone = 'warning' | 'info' | 'success' | 'neutral';

const STATUS_MAP: Record<
  StatusTone,
  { bg: string; color: string; dot: string }
> = {
  warning: { bg: '#fff4e0', color: '#b45309', dot: '#f59e0b' },
  info: { bg: '#e0ecff', color: '#1d4ed8', dot: '#3b82f6' },
  success: { bg: '#e6f3ec', color: '#006633', dot: '#16a34a' },
  neutral: { bg: '#f1f5f9', color: '#475569', dot: '#94a3b8' },
};

export const StatusDropdown = styled.div`
  position: relative;
`;

export const StatusPill = styled.button<{
  $tone: StatusTone;
  $clickable?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid transparent;
  font-size: 12px;
  font-weight: 700;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: filter 0.15s ease, box-shadow 0.15s ease;

  ${({ $tone }) => css`
    background: ${STATUS_MAP[$tone].bg};
    color: ${STATUS_MAP[$tone].color};
  `}

  &:hover {
    ${({ $clickable }) =>
      $clickable &&
      css`
        filter: brightness(0.97);
        box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
      `}
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${({ $tone }) => STATUS_MAP[$tone].dot};
  }

  svg {
    margin-left: 2px;
  }
`;

export const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  min-width: 220px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
  padding: 6px;
  z-index: 20;
`;

export const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: 8px;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #334155;
  font-weight: 500;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
    color: #006633;
  }

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }
`;

export const IconButton = styled.button<{ $variant?: 'ghost' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    ${({ $variant }) =>
      $variant === 'danger'
        ? css`
            background: #fef2f2;
            border-color: #b91c1c;
            color: #b91c1c;
          `
        : css`
            background: #f8fafc;
            border-color: #006633;
            color: #006633;
          `}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ChatBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 20px 8px;
  background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
  display: flex;
  flex-direction: column;

  /* Scrollbar moderno */
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #cbd5e1;
  }
`;

export const DaySeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 16px 0 12px;

  span {
    background: #f1f5f9;
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
    padding: 4px 12px;
    border-radius: 999px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
`;

export const EmptyChat = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #94a3b8;
  font-size: 13px;

  .emoji {
    font-size: 34px;
    margin-bottom: 4px;
  }

  .title {
    color: #475569;
    font-weight: 600;
    font-size: 14px;
  }
`;

export const ChatFooter = styled.footer`
  border-top: 1px solid #eef2f7;
  background: #ffffff;
  padding: 12px 16px;
`;

export const Composer = styled.form<{ $disabled?: boolean }>`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 8px 8px 8px 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #006633;
    box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.1);
    background: #ffffff;
  }

  textarea {
    flex: 1;
    resize: none;
    border: none;
    outline: none;
    background: transparent;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.45;
    color: #0f172a;
    max-height: 140px;
    padding: 8px 0;

    &::placeholder {
      color: #94a3b8;
    }
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.7;
      pointer-events: none;
    `}
`;

export const SendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  border: none;
  background: #006633;
  color: #ffffff;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: #00451f;
  }

  &:active:not(:disabled) {
    transform: scale(0.96);
  }

  &:disabled {
    background: #cbd5e1;
    cursor: not-allowed;
  }
`;

export const ComposerHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 6px;
  padding: 0 4px;
  font-size: 11px;
  color: #94a3b8;

  kbd {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    font-family: inherit;
    font-size: 10px;
    color: #475569;
    font-weight: 600;
  }
`;

export const FinishedBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  margin: 0 2px;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  font-size: 12px;
  font-weight: 500;

  strong {
    color: #0f172a;
    font-weight: 700;
  }
`;

/* Modal (shared) */
export const ModalCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  label {
    font-size: 12px;
    font-weight: 600;
    color: #475569;
  }

  textarea {
    width: 100%;
    min-height: 110px;
    border-radius: 10px;
    border: 1px solid #e2e8f0;
    padding: 10px 12px;
    font-family: inherit;
    font-size: 13px;
    color: #0f172a;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: #006633;
      box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.1);
    }
  }

  p {
    margin: 0;
    color: #475569;
    font-size: 14px;
    line-height: 1.5;
  }
`;
